import { DISABLED_WEBHOOK, ENABLED_WEBHOOK } from '../constants';
import { AUTH_TYPES, SystemWebhookFormState, WebhookParamProps } from '../types';
import { getEnabledEvents } from '../utils';

/**
 * **authTypeRequiresToken**
 * Checks if the given authentication type requires a token
 * @param { String } authType: Authentication type (NoAuth, Basic, or OAuth2)
 * @returns { Boolean } True if the authentication type requires token, false otherwise
 */
export const authTypeRequiresToken = (authType: AUTH_TYPES) =>
  authType === AUTH_TYPES.BASIC || authType === AUTH_TYPES.OAUTH_2;

/**
 * refreshEnabledEvents
 * Maps list of events to an object with event keys and boolean values
 * @param { Object } webhookParams: webhook information
 * @returns { Object } Mapping of event names to boolean indicating if they are enabled
 */
export const refreshEnabledEvents = (webhookParams: WebhookParamProps) =>
  getEnabledEvents(webhookParams).reduce(
    (enabledEvents, event) => ({
      ...enabledEvents,
      [event]: true,
    }),
    {},
  );

/**
 * generateStateFromWebhookParams
 * Creates state object for edit system webhooks page from webhook params
 * @param { Object } webhookParams: webhook information
 * @returns { Object } Mapping of webhook information to how it will be stored in state
 */
export const generateStateFromWebhookParams = (
  params: WebhookParamProps,
): SystemWebhookFormState => ({
  authToken: params.authToken || '',
  authType: params.authType || AUTH_TYPES.NO_AUTH,
  enabled: params.enabled
    ? (ENABLED_WEBHOOK.value as 'enabled')
    : (DISABLED_WEBHOOK.value as 'disabled'),
  enabledEvents: refreshEnabledEvents(params),
  endpoint: params.endpoint || '',
  headers: params.headers || [],
  rateLimitPerSecond: params.rateLimitPerSecond || null,
  /** If SystemWebhookAdvancedFiltering FF is on, then the Webhook object should include the excludeListUpdates field,
   * in which case we want to ensure it is loaded.
   */
  excludeListUpdates: params.excludeListUpdates,
});
